import { useMutation } from "@tanstack/react-query";
import { apiV1Endpoint } from "../../../../../../../api/v1";
import { ApiResponse } from "../../../../../../../types/api-response";
import axios from "../../../../../../../utils/axiosInceptor";
import { BillingSchedule, BreakdownMode, ServiceCharge } from "./types";

type GenerateScheduleBreakdownResult = BillingSchedule[] | ServiceCharge[];

type CommercialPaymentPlanItem = {
  noOfYear: number;
  installments: number;
  torPeriod?: number;
  rentType: "fixed" | "areaSqft";
  annualRent: string | number;
  securityDeposit?: string | number;
  rentValue?: string;
  unitAreaInSqft?: string;
  rentCommencementDate?: string;
  lastEndDate?: string;
};

type ServiceChargeItem = {
  noOfYear: number;
  installments: number;
  rentType: "fixed" | "areaSqft" | "percentage";
  annualRent: string | number;
  rentValue?: string;
  percentageValue?: string;
  unitAreaInSqft?: string;
  lastEndDate?: string;
};

type BaseRentBreakdownRequest = {
  leaseId: string;
  generate: BreakdownMode;
  commercialPaymentPlan: CommercialPaymentPlanItem[];
};

type ServiceChargeRequest = {
  leaseId: string;
  generate: "serviceCharge";
  serviceCharge: ServiceChargeItem;
};

type GenerateScheduleBreakdownRequest = BaseRentBreakdownRequest | ServiceChargeRequest;

const generateScheduleBreakdownFn = async (payload: GenerateScheduleBreakdownRequest) => {
  const url = `${apiV1Endpoint}/booking/monthlyBillingSchedule`;
  const response = await axios.post<ApiResponse<GenerateScheduleBreakdownResult>>(url, payload);
  return response.data.result;
};

type UseGenerateScheduleBreakdownOptions = {
  onSuccess?: (data: GenerateScheduleBreakdownResult) => void;
  onError?: (error: unknown) => void;
};

export const useGenerateScheduleBreakdown = (options: UseGenerateScheduleBreakdownOptions = {}) => {
  const { onSuccess, onError } = options;

  const { mutateAsync, isPending } = useMutation({
    mutationFn: generateScheduleBreakdownFn,
    onSuccess,
    onError,
  });

  function generateScheduleBreakdown(payload: BaseRentBreakdownRequest): Promise<BillingSchedule[]>;
  function generateScheduleBreakdown(payload: ServiceChargeRequest): Promise<ServiceCharge[]>;

  function generateScheduleBreakdown(payload: GenerateScheduleBreakdownRequest) {
    return mutateAsync(payload);
  }

  return {
    generateScheduleBreakdown,
    isPendingGenerateScheduleBreakdown: isPending,
  };
};
